import React, { useState, useEffect } from 'react';
import { getAuthenticatedAxios } from '../../utils/api';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Network, 
  Users, 
  AlertTriangle,
  Link2,
  LayoutGrid,
  Maximize2
} from 'lucide-react';
import NetworkGraph from './NetworkGraph';
import FarmsList from './FarmsList';

import { getApiUrl } from '../../utils/api';
import { useTokenConfig } from '../../contexts/TokenConfigContext';

const API_URL = getApiUrl();

const HolderConnections = () => {
  // Token configuration from context
  const { tokenAddress: BUYT_TOKEN, tokenSymbol } = useTokenConfig();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [viewMode, setViewMode] = useState('both');
  const [holderLimit, setHolderLimit] = useState(1000); // По умолчанию все
  
  const loadConnections = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(
        `${API_URL}/api/holder-connections/${BUYT_TOKEN}?limit=${holderLimit}&force_refresh=${forceRefresh}`
      );
      
      setData(response.data);
      
      const message = response.data.message;
      
      if (message) {
        toast.success(message);
      } else if (response.data.stats.total_farms > 0) {
        toast.success(`Найдено ${response.data.stats.total_farms} кластеров`);
      } else {
        toast.info('Кластеры не обнаружены');
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
      toast.error('Ошибка: ' + (typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg)));
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadConnections();
  }, []);
  
  const handleNodeClick = (node) => {
    if (node.farm_id) {
      setSelectedFarm(node.farm_id);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0d0d1a] p-3 md:p-6">
      {/* Header - responsive */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 md:mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <Network className="h-5 w-5 md:h-6 md:w-6 text-purple-400" />
            Кластеры
          </h1>
          <p className="text-gray-500 text-xs md:text-sm mt-1">
            Анализ связей между кошельками
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Limit selector */}
          <select
            value={holderLimit}
            onChange={(e) => setHolderLimit(Number(e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-xs md:text-sm"
          >
            <option value={100}>100</option>
            <option value={300}>300</option>
            <option value={500}>500</option>
            <option value={1000}>1000 (все)</option>
            <option value={2000}>2000</option>
            <option value={5000}>5000</option>
          </select>
          
          {/* View toggle - hidden on mobile */}
          <div className="hidden md:flex bg-white/10 rounded-lg p-0.5">
            <button
              className={`p-1.5 rounded ${viewMode === 'graph' ? 'bg-white/20 text-white' : 'text-gray-400'}`}
              onClick={() => setViewMode('graph')}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
            <button
              className={`p-1.5 rounded ${viewMode === 'both' ? 'bg-white/20 text-white' : 'text-gray-400'}`}
              onClick={() => setViewMode('both')}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white/20 text-white' : 'text-gray-400'}`}
              onClick={() => setViewMode('list')}
            >
              <Users className="h-4 w-4" />
            </button>
          </div>
          
          <button 
            onClick={() => loadConnections(false)} 
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            title="Синхронизировать (обновит только изменённых)"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Синхронизировать</span>
          </button>
          <button 
            onClick={() => loadConnections(true)} 
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs disabled:opacity-50"
            title="Полный пересчёт всех холдеров"
          >
            <span>Пересчитать всё</span>
          </button>
        </div>
      </div>
      
      {/* Status message */}
      {data && data.message && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
          <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
          {data.message}
        </div>
      )}
      
      {/* Stats - responsive grid */}
      {data && (
        <div className="grid grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white/5 border border-white/10 rounded-lg p-2 md:p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 md:h-5 md:w-5 text-blue-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-lg md:text-2xl font-bold text-white truncate">{data.stats.total_holders}</div>
                <div className="text-[10px] md:text-xs text-gray-500 truncate">Всего</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-2 md:p-4">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 md:h-5 md:w-5 text-purple-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-lg md:text-2xl font-bold text-white truncate">{data.stats.total_connections}</div>
                <div className="text-[10px] md:text-xs text-gray-500 truncate">Связей</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-2 md:p-4">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 md:h-5 md:w-5 text-teal-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-lg md:text-2xl font-bold text-white truncate">{data.stats.connected_holders}</div>
                <div className="text-[10px] md:text-xs text-gray-500 truncate">Связанных</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-2 md:p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 md:h-5 md:w-5 shrink-0 ${data.stats.total_farms > 0 ? 'text-red-400' : 'text-green-400'}`} />
              <div className="min-w-0">
                <div className="text-lg md:text-2xl font-bold text-white truncate">{data.stats.total_farms}</div>
                <div className="text-[10px] md:text-xs text-gray-500 truncate">Кластеров</div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <RefreshCw className="h-8 w-8 text-purple-400 animate-spin mb-4" />
          <p className="text-gray-400">Анализируем связи...</p>
        </div>
      ) : data ? (
        <>
          {/* Mobile: stacked layout */}
          <div className="md:hidden space-y-4">
            {/* Graph */}
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
                <span className="text-sm text-white flex items-center gap-2">
                  <Network className="h-4 w-4 text-purple-400" />
                  Граф связей
                </span>
                <span className="text-xs text-gray-500">
                  {data.stats.connected_holders} кошельков • {data.stats.total_connections} связей
                </span>
              </div>
              <NetworkGraph 
                graph={data.graph}
                farms={data.farms}
                onNodeClick={handleNodeClick}
                selectedFarm={selectedFarm}
              />
            </div>
            
            {/* Clusters */}
            {data.farms.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <div className="px-3 py-2 border-b border-white/10">
                  <span className="text-sm text-white flex items-center gap-2">
                    <Users className="h-4 w-4 text-pink-400" />
                    Кластеры ({data.farms.length})
                  </span>
                </div>
                <div className="p-3 max-h-[400px] overflow-y-auto">
                  <FarmsList 
                    farms={data.farms}
                    onFarmSelect={setSelectedFarm}
                    selectedFarm={selectedFarm}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Desktop: side by side */}
          <div className="hidden md:grid gap-4" style={{
            gridTemplateColumns: viewMode === 'graph' ? '1fr' : viewMode === 'list' ? '1fr' : '2fr 1fr'
          }}>
            {/* Graph */}
            {(viewMode === 'graph' || viewMode === 'both') && (
              <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
                  <span className="text-sm text-white flex items-center gap-2">
                    <Network className="h-4 w-4 text-purple-400" />
                    Граф связей
                  </span>
                  <span className="text-xs text-gray-500">
                    {data.stats.connected_holders} кошельков • {data.stats.total_connections} связей
                  </span>
                </div>
                <NetworkGraph 
                  graph={data.graph}
                  farms={data.farms}
                  onNodeClick={handleNodeClick}
                  selectedFarm={selectedFarm}
                />
              </div>
            )}
            
            {/* Clusters */}
            {(viewMode === 'list' || viewMode === 'both') && data.farms.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden flex flex-col" style={{ maxHeight: 600 }}>
                <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between shrink-0">
                  <span className="text-sm text-white flex items-center gap-2">
                    <Users className="h-4 w-4 text-pink-400" />
                    Кластеры ({data.farms.length})
                  </span>
                  <span className="text-xs text-gray-500">
                    Макс: {data.stats.largest_farm_size}
                  </span>
                </div>
                <div className="p-3 overflow-y-auto flex-1">
                  <FarmsList 
                    farms={data.farms}
                    onFarmSelect={setSelectedFarm}
                    selectedFarm={selectedFarm}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-5xl mb-4">🗺️</div>
          <p className="text-gray-400">Нажмите "Анализировать"</p>
        </div>
      )}
    </div>
  );
};

export default HolderConnections;
